import React, { FC, useContext, useCallback } from 'react';
import { useQueryClient, useMutation } from 'react-query';
import {
  TableRow,
  TableCell,
  Typography,
  MenuList,
  MenuItem,
} from '@mui/material';
import { MoreVert } from '@mui/icons-material';

import { genericError, changesSaved } from 'store/Layout';
import { useDialogContext } from 'store/Dialog';
import { cacheKeys } from 'config';
import { LocalizationContext } from 'contexts';
import { getAuthGroupTranslation } from 'helpers/translationHelpers';
import { usersClient } from 'clients/users/usersClient';
import { UserRowType } from 'clients/users/userClient.types';
import { useGetAuthGroups } from 'shared/hooks/useGetAuthGroups';
import { IconButtonDropdown } from 'shared/components/interactive/ButtonDropdown/IconButtonDropdown';

export const UserRow: FC<UserRowType> = (user) => {

  const { dictionary } = useContext(LocalizationContext);
  const { asyncConfirmation } = useDialogContext();

  const queryClient = useQueryClient();

  const deleteUser = useMutation(usersClient.deleteUser, {
    mutationKey: cacheKeys.users.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries(cacheKeys.users.getUsers);
      queryClient.removeQueries([cacheKeys.users.getUser, {
        id: user.id,
      }]);
      changesSaved(dictionary);
    },
    onError: () => {
      genericError(dictionary);
    },
  });
  const changeUserGroup = useMutation((data: {
		userId: number;
		groupId: number
	}) => usersClient.changeUserGroup(data), {
    mutationKey: cacheKeys.users.changeUserGroup,
    onSuccess: () => {
      queryClient.invalidateQueries(cacheKeys.users.getUsers);
      queryClient.invalidateQueries([cacheKeys.users.getUser, { id: user.id }]);
      changesSaved(dictionary);
    },
    onError: () => {
      genericError(dictionary);
    },
  });


  const onDelete = useCallback(async () => {
    const userConfirmed = await asyncConfirmation({ title: dictionary.user.edit.deleteConfirmation });
    if (!user || !userConfirmed) {
      return false;
    }
    return deleteUser.mutate({
      id: user.id,
    });
  }, [user, deleteUser, asyncConfirmation, dictionary]);

  const { data, status } = useGetAuthGroups();

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const groupId = parseInt(e.target.value, 10);
    changeUserGroup.mutate({ userId: user.id, groupId });
  };

  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <Typography variant="subtitle1">{user.displayName}</Typography>
        <Typography color="primary" variant="subtitle2">{user.email}</Typography>
      </TableCell>
      <TableCell>{user.jobTitle || dictionary.team.unknownLabel}</TableCell>
      {
        user.editable && !user.isCurrentUser ? (
          <TableCell>
            <select defaultValue={user?.authGroup?.id || '-'} onChange={handleSelectChange}>
              {status === 'success' && data.data.results.map((group: any) => (
                <option key={group.id} value={group.id}>
                  {getAuthGroupTranslation(dictionary, group.name)}
                </option>
              ))}
            </select>
          </TableCell>
        ) :
          (
            <TableCell>{getAuthGroupTranslation(dictionary, user?.authGroup?.name) || dictionary.team.unknownLabel}</TableCell>
          )
      }


      <TableCell align="right">
        <IconButtonDropdown
          placement="bottom-start"
          buttonProps={{
            color: 'secondary',
          }}
          content={({ closeDropdown }) => (
            <MenuList>
              <MenuItem
                onClick={() => {
                  onDelete();
                  closeDropdown();
                }}
              >
                {dictionary.user.edit.buttonDelete}
              </MenuItem>
            </MenuList>
          )}
        >
          <MoreVert/>
        </IconButtonDropdown>
      </TableCell>
    </TableRow>
  )
  ;
};